import { usePathname } from 'next/navigation';
import React, { useEffect, useState } from 'react';

import { useModal } from '@/components/hooks/useModal';
import { ITag } from '@/components/sections/content-hub/tag';
import { Button } from '@/components/shared/buttons/button';
import { ButtonGroup } from '@/components/shared/buttons/button-group';
import { SocialMediaShareButtons } from '@/components/shared/buttons/social-media-share-buttons';
import { Text } from '@/components/shared/element/text';
import { Box } from '@/components/shared/layout/box';
import { Container } from '@/components/shared/layout/container';
import { Grid } from '@/components/shared/layout/grid';
import { Section } from '@/components/shared/layout/section';
import { Image } from '@/components/shared/media/image';
import { generateTabletAndMobileImageSources } from '@/components/shared/media/image/generate-sources';
import { ImageProps } from '@/components/shared/media/image/Image.props';
import { Breadcrumb } from '@/components/shared/navigation/breadcrumb';
import {
  formatDateFull,
  removeEmptyTags,
  youtubeURL,
} from '@/components/shared/utility/format';
import { MENU_CV_CONTENT_HUB_EXPERT } from '@/contents/link';
import { styled } from '@/stitches.config';
import { trackContentHub } from '@/tag-manager';
import { IButton } from '@/types/shared';

const ContentItem = styled('div', {
  maxWidth: '100%',
  overflow: 'hidden',
  iframe: {
    position: 'absolute',
    left: 0,
    top: 0,
    width: '100%',
    height: '100%',
  },
  variants: {
    size: {
      normal: {
        '@lg': { gridColumn: '2 / span 10' },
        '@xl': { gridColumn: '3 / span 8' },
      },
      full: {
        '@lg': { gridColumn: '1 / span 12' },
      },
    },
  },
  defaultVariants: {
    size: 'normal',
  },
});
export interface IContentHubItem {
  type: 'inspiration' | 'experiences' | 'expert';
  uid: string;
  metatag: {
    title: string;
    description: string;
    img: {
      url: string;
    };
  };
  category: {
    name: string;
    url: string;
    active: boolean;
  }[];
  beginDate: string;
  endDate: string;
  tag: ITag[];
  url: string;
  title: string;
  author: string;
  excerpt: string;
  detail_title?: string;
  detail: string;
  image: ImageProps;
  imageDetail: ImageProps;
  youtube_id: string;
  buttons?: IButton[];
  album_title?: string;
  albums?: ImageProps[];
}

const ContentHubGridDetailSection: React.FC<IContentHubItem> = (
  props: IContentHubItem
) => {
  const pathname = usePathname();
  const [currentURL, setCurrentURL] = useState<string>('');
  const [videoSrc, setVideoSrc] = useState<string>('');
  const { api: modal, component: modalComponent } = useModal({
    type: 'gallery',
  });

  const onClickAlbum = (index: number) => {
    modal.open({
      imageItems: props.albums as any[],
      activeSlideIndex: index,
    });
  };

  useEffect(() => {
    setCurrentURL(location.href);

    setVideoSrc('');
    props.youtube_id
      ? setVideoSrc(youtubeURL(props.youtube_id))
      : setVideoSrc('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  useEffect(() => {
    const tags = props.tag.map((o) => o.url);
    if (tags.length) {
      trackContentHub({
        tags: tags,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Breadcrumb
        items={[
          {
            label: props.category.filter((o) => o.active)[0].name,
            url: props.category.filter((o) => o.active)[0].url,
          },
          {
            label: props.title,
          },
        ]}
      />
      <Section size="xl" test="content_hub_detail">
        <Grid rowGap={{ '@initial': '10', '@lg': '14' }}>
          <Box>
            <Container>
              <Grid templateColumns={{ '@lg': '12' }} columnGap="2">
                <ContentItem>
                  {/* eslint-disable-next-line react/jsx-max-depth */}
                  <Grid rowGap="4">
                    {!!props.beginDate && (
                      <Text as="p" size="sm" variant="text-gray">
                        {formatDateFull(props.beginDate)}
                      </Text>
                    )}
                    {!!props.title && (
                      <Text as="h1" size="h4" font="bold">
                        {props.title}
                      </Text>
                    )}
                    {!!props.excerpt && (
                      <Text
                        as="div"
                        size="h5"
                        wrap
                        dangerouslySetInnerHTML={{
                          __html: removeEmptyTags(props.excerpt),
                        }}
                      />
                    )}
                  </Grid>
                </ContentItem>
              </Grid>
            </Container>
          </Box>
          <Box>
            {props.youtube_id ? (
              <Container maxWidth="lg">
                <Grid templateColumns={{ '@lg': '12' }} columnGap="2">
                  <ContentItem size="full">
                    <Box
                      position="relative"
                      css={{
                        aspectRatio: '16 / 9',
                      }}
                    >
                      <iframe
                        src={videoSrc}
                        frameBorder="0"
                        allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        title={props.title}
                      />
                    </Box>
                  </ContentItem>
                </Grid>
              </Container>
            ) : (
              <Container
                maxWidth={props.image.width > props.image.height ? 'lg' : 'md'}
              >
                <Grid templateColumns={{ '@lg': '12' }} columnGap="2">
                  <ContentItem
                    size={
                      props.image.width > props.image.height ? 'full' : 'normal'
                    }
                  >
                    <Image
                      priority
                      placeholder="shimmer"
                      src={props.image.src}
                      alt={props.image.alt}
                      width={props.image.width}
                      height={props.image.height}
                      layout="responsive"
                      sources={[
                        {
                          srcset: props.image.src,
                          media: { minWidth: 1024 },
                          options: {
                            width:
                              props.image.width > props.image.height
                                ? 1280
                                : 828,
                            trim: 2,
                          },
                        },
                        ...generateTabletAndMobileImageSources({
                          mobileSrc: props.image.src,
                          mobileOptions: {
                            trim: 2,
                          },
                        }),
                      ]}
                    />
                  </ContentItem>
                </Grid>
              </Container>
            )}
          </Box>
          <Box>
            <Container>
              <Grid templateColumns={{ '@lg': '12' }} columnGap="2">
                <ContentItem>
                  <Grid rowGap="8">
                    {!!props.detail_title && (
                      <Text size="text-body-1" font="bold">
                        {props.detail_title}
                      </Text>
                    )}
                    {!!props.detail && (
                      <Text
                        as="div"
                        size="text-body-1"
                        wrap
                        dangerouslySetInnerHTML={{
                          __html: removeEmptyTags(props.detail),
                        }}
                      />
                    )}
                    {!!props.album_title &&
                      !!props.albums &&
                      props.albums.length > 0 && (
                        <Text size="text-body-1" font="bold">
                          {props.album_title}
                        </Text>
                      )}
                    {!!props.albums && props.albums.length > 0 && (
                      <Grid
                        templateColumns={{ '@initial': '3', '@md': '4' }}
                        gap={{ '@initial': '3', '@lg': '4' }}
                      >
                        {props.albums.map((o, i) => (
                          <Box
                            key={i}
                            onClick={() => onClickAlbum(i)}
                            cursor="pointer"
                          >
                            <Image
                              placeholder="shimmer"
                              hoverEffect="zoom"
                              src={o.src}
                              alt={o.alt}
                              width={256}
                              height={256}
                              layout="responsive"
                              aspectRatio="1by1"
                              sources={[
                                {
                                  srcset: o.src,
                                  media: { minWidth: 768 },
                                  options: { width: 256, crop: '1:1' },
                                },
                                {
                                  srcset: o.src,
                                  media: { maxWidth: 767 },
                                  options: { width: 128, crop: '1:1' },
                                },
                              ]}
                            />
                          </Box>
                        ))}
                      </Grid>
                    )}
                    {!!props.buttons && props.buttons.length > 0 && (
                      <ButtonGroup
                        buttons={props.buttons}
                        size="md"
                        tracking={{
                          dataTrack: 'content-hub-list',
                          dataTrackValue: props.title,
                        }}
                      />
                    )}
                    {/* eslint-disable-next-line react/jsx-max-depth */}
                    <SocialMediaShareButtons
                      id="copy-link-desktop"
                      title={props.title}
                      url={currentURL}
                      dataTrack="contenthub-share"
                    />
                  </Grid>
                </ContentItem>
              </Grid>
            </Container>
          </Box>
          <Box>
            <Container>
              <Grid templateColumns={{ '@lg': '12' }} columnGap="2">
                <ContentItem>
                  <ButtonGroup justify="center">
                    {/* eslint-disable-next-line react/jsx-max-depth */}
                    <Button
                      label="กลับไปบทความทั้งหมด"
                      href={MENU_CV_CONTENT_HUB_EXPERT.url}
                      variant="button-gray"
                      icon="arrow-back"
                      border
                    />
                  </ButtonGroup>
                </ContentItem>
              </Grid>
            </Container>
          </Box>
        </Grid>
      </Section>
      {modalComponent}
    </>
  );
};
ContentHubGridDetailSection.displayName = 'ContentHubGridDetailSection';
export default ContentHubGridDetailSection;
